import type {
  TraderDataset,
  TraderDirection,
  TraderPosition,
  TraderTrade,
} from '@/modules/traders-api';

// The readings a whale list is built from: which profit a list ranks by,
// how an asset's lists merge, and how a position's moves read as buys and
// sells.

// The profits a list ranks by: a dataset's own, or the open part's at the
// snapshot's mark price, which no dataset ranks by — the unrealized list is
// the union of the two datasets, ranked here.
export type ProfitKind = TraderDataset | 'unrealized';

export function profitOf(row: TraderPosition, kind: ProfitKind): number | null {
  return (
    kind === 'realized' ? row.realizedProfitUsd
    : kind === 'unrealized' ? row.unrealizedProfitUsd
    : row.totalProfitUsd
  );
}

// The rows by the kind's profit, largest first, wallets without a figure
// last, ties by address so the order is total. The backend ranks by
// realized profit alone.
export function sortByProfit<T extends { position: TraderPosition }>(
  rows: readonly T[],
  kind: ProfitKind,
): T[] {
  return [...rows].sort((a, b) => {
    const pa = profitOf(a.position, kind);
    const pb = profitOf(b.position, kind);
    if (pa === null && pb === null) {
      return a.position.address.localeCompare(b.position.address);
    }
    if (pa === null) return 1;
    if (pb === null) return -1;
    if (pa !== pb) return pb - pa;
    return a.position.address.localeCompare(b.position.address);
  });
}

// An asset's lists hold ten wallets per direction each and overlap; the
// union keeps one entry per wallet and direction, the first occurrence
// winning.
export function unionWallets<T extends { position: TraderPosition }>(
  lists: readonly (readonly T[])[],
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const list of lists) {
    for (const wallet of list) {
      const key = `${wallet.position.address}-${wallet.position.direction}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(wallet);
    }
  }
  return out;
}

// A long is bought open and sold closed; a short the other way round.
export function tradeSide(
  direction: TraderDirection,
  side: TraderTrade['side'],
): 'buy' | 'sell' {
  return (direction === 'LONG') === (side === 'OPEN') ? 'buy' : 'sell';
}

// Coins bought over the whole history: everything opened for a long,
// everything closed for a short.
export function boughtQty(row: TraderPosition): number | null {
  return row.direction === 'LONG' ? row.openedQty : row.closedQty;
}

export function soldQty(row: TraderPosition): number | null {
  return row.direction === 'LONG' ? row.closedQty : row.openedQty;
}

export function countSides(
  trades: readonly TraderTrade[],
  direction: TraderDirection,
): { buy: number; sell: number } {
  let buy = 0;
  let sell = 0;
  for (const trade of trades) {
    if (tradeSide(direction, trade.side) === 'buy') buy += 1;
    else sell += 1;
  }
  return { buy, sell };
}

// Coins still held; null for a closed-out position and for a history
// delivered without its start (the remainder reads negative there).
export function openQty(row: TraderPosition): number | null {
  const qty = row.remainingQty ?? 0;
  return qty > 0 ? qty : null;
}

// The price and moment of a wallet's newest priced fill — what its open
// position is valued at, the one price the exports state for it.
export type PricedFill = { price: number; tradedAt: string };

// The newest fill of a history that carries a price. Fills at the same
// instant keep the first delivered; null for a history without a priced
// fill (an export delivered without execution prices).
export function lastPricedFill(
  trades: readonly TraderTrade[],
): PricedFill | null {
  let last: (PricedFill & { at: number }) | null = null;
  for (const trade of trades) {
    if (trade.price === null) continue;
    const at = Date.parse(trade.tradedAt);
    if (Number.isNaN(at)) continue;
    if (last === null || at > last.at) {
      last = { price: trade.price, tradedAt: trade.tradedAt, at };
    }
  }
  return last === null ? null : { price: last.price, tradedAt: last.tradedAt };
}

const DAY_S = 86_400;
const WINDOW_DAYS = 30;
const MAX_WINDOW_DAYS = 365;

// The wallet chart's frame, in unix seconds: it opens on the latest
// WINDOW_DAYS of daily candles, reaching back to the day before the last fill
// when that is older — the last trade stays in view — and at most
// MAX_WINDOW_DAYS; the candles fetched start at the UTC midnight before the
// window or the first fill, whichever is earlier, inside the same year.
export function chartFrame(
  span: { from: number; to: number } | null,
  now: number,
): {
  window: {
    // unread-field-allow: consumed structurally — the window passes whole as CandleChart's structurally-typed `range` prop, which reads both bounds itself
    from: number;
    to: number;
  };
  fetchFrom: number;
} {
  const floor = now - MAX_WINDOW_DAYS * DAY_S;
  const latest = now - WINDOW_DAYS * DAY_S;
  const reach = span === null ? latest : Math.min(latest, span.to - DAY_S);
  const from = Math.max(reach, floor);
  const earliest = span === null ? from : Math.min(from, span.from - DAY_S);
  return {
    window: { from, to: now },
    fetchFrom: Math.floor(Math.max(earliest, floor) / DAY_S) * DAY_S,
  };
}

// The list's wallet label: the first four hex digits and the last four
// around an ellipsis.
export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}
